<template>
	<div class="time-entry-done-input" :class="classAddons">
		<SuiTimeInput
			ref="$timeEntryInput"
			:model-value="timeEntryValue"
			class="time-entry-done-input__input"
			size="small"
			:align="SuiInputAlign.Center"
			full-width
			hide-clear-button
			:hours-per-day="hoursPerDay"
			@submit="onTimeEntrySave"
			@focusout="onFocusOut"
			@click="focusAndSelectAll(false)"
			@update:validated-value="onUpdateValidatedValue"
			@keydown.esc="timeEntryValue = defaultTimeEntryValue"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue';
import { SuiTimeInput, SuiInputAlign } from '@developers/scoro-ui';
import { type ValueOf } from '@/types';
import { timeToSeconds } from '@/utils/time';
import { type TIME_ENTRY_STATE } from '../types';
import { useSettings } from '@/composables';
import { Setting } from '@/constants/settings';

export type TimeEntryInputProps = {
	defaultTimeEntryValue: string;
	state?: ValueOf<typeof TIME_ENTRY_STATE>;
};

const props = defineProps<TimeEntryInputProps>();

const emit = defineEmits<{
	(e: 'add', duration: number): void;
	(e: 'blur', duration: number): void;
	(e: 'modify', duration: number): void;
	(e: 'input-valid-change', isValid: boolean): void;
}>();

const { setting } = useSettings();

const hoursPerDay = computed(() =>
	Number(setting(Setting.SYSTEM_DAY_UNIT_SUB_VALUE))
);

const $timeEntryInput = ref<typeof SuiTimeInput>();
const $input = computed<HTMLInputElement | undefined>(
	() => $timeEntryInput.value?.$refs.$suiInput.$refs.input
);

const timeEntryValue = ref(props.defaultTimeEntryValue);
const isEnteredInputValueValid = ref(true);

const focusAndSelectAll = async (isFocused: boolean) => {
	const inputEl = $input.value;

	if (!inputEl) {
		return;
	}

	if (isFocused) {
		inputEl.blur();
		await nextTick();
	}

	inputEl.focus();

	await nextTick();
	inputEl.select();
};

const onUpdateValidatedValue = ({
	value,
	isValid,
}: {
	value: string;
	isValid: boolean;
}) => {
	timeEntryValue.value = value;
	isEnteredInputValueValid.value = isValid;
	emit('input-valid-change', isValid);
	if (!isValid) {
		focusAndSelectAll(true);
	}
};

const onFocusOut = () => {
	if (isEnteredInputValueValid.value) {
		emit('blur', timeToSeconds(timeEntryValue.value));
	} else {
		timeEntryValue.value = props.defaultTimeEntryValue;
		focusAndSelectAll(true);
	}
};

const onTimeEntrySave = () => {
	if (isEnteredInputValueValid.value) {
		emit('add', timeToSeconds(timeEntryValue.value));
	} else {
		focusAndSelectAll(true);
	}
};

const classAddons = computed(() => {
	return {
		[`time-entry-done-input--${props.state}`]: props.state,
	};
});

onMounted(() => {
	if ($input.value) {
		focusAndSelectAll(false);
	}
});
</script>

<style lang="scss">
.time-entry-done-input {
	$parent: &;

	&--success {
		animation: success 0.2s 0.6s ease forwards;

		#{$parent}__input {
			.sui-input {
				--sui-input-color-text: #{$green-80};

				background: $green-10;
				border-color: $green-30;
			}
		}
	}

	&--error {
		#{$parent}__input {
			.sui-input {
				animation: error 0.8s ease forwards;
			}
		}
	}

	@keyframes success {
		0% {
			opacity: 1;
			transform: translateY(0);
		}

		100% {
			opacity: 0;
			transform: translateY(1rem);
		}
	}

	@keyframes error {
		30% {
			border-color: $red-40;
		}

		50% {
			border-color: $red-20;
		}

		80% {
			border-color: $red-50;
		}
	}
}
</style>
